import React from 'react';
import { motion } from 'framer-motion';
import { Activity, ArrowUpRight, Monitor, RefreshCw, Smartphone } from 'lucide-react';
import { formatDuration } from '../utils';
import { CircularProgress } from './CircularProgress';
import type { PerfScores } from '../types';

interface DiagnosticsPanelProps {
  siteUrl: string;
  perfDevice: 'mobile' | 'desktop';
  onSetPerfDevice: (device: 'mobile' | 'desktop') => void;
  perfData: { mobile: PerfScores | null; desktop: PerfScores | null };
  loadingPerf: boolean;
  onRefresh: () => void;
}

export const DiagnosticsPanel: React.FC<DiagnosticsPanelProps> = ({
  siteUrl,
  perfDevice,
  onSetPerfDevice,
  perfData,
  loadingPerf,
  onRefresh,
}) => {
  const scores = perfData[perfDevice] || { perf: 0, acc: 0, best: 0, seo: 0 };

  return (
    <div className="bg-card border border-border rounded-xl p-5 shadow-sm w-full h-full flex flex-col">
      <div className="flex items-center justify-between mb-10">
        <div className="flex items-center gap-2.5">
          <div className="p-2 rounded-lg bg-orange-500/10">
            <Activity className="w-4 h-4 text-orange-500" />
          </div>
          <div className="flex flex-col">
            <h3 className="text-sm font-bold text-foreground uppercase tracking-tight leading-none">Diagnostics</h3>
            {perfData[perfDevice]?.fetchedAt && (
              <p className="text-[10px] font-medium text-muted-foreground/60 mt-1">
                {formatDuration(perfData[perfDevice]!.fetchedAt!)} ago
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={onRefresh}
            disabled={loadingPerf}
            className={`text-muted-foreground hover:text-foreground transition-colors p-2 rounded-lg hover:bg-muted ${loadingPerf ? 'opacity-50' : ''}`}
            title="Refresh All Diagnostics"
          >
            <RefreshCw className={`w-4 h-4 ${loadingPerf ? 'animate-spin' : ''}`} />
          </button>

          <div className="h-6 w-px bg-border mx-1" />

          <div className="flex bg-muted/40 p-1 rounded-xl relative">
            <button
              onClick={() => onSetPerfDevice('mobile')}
              className={`flex items-center justify-center w-9 h-7 text-[10px] font-bold transition-all relative z-10 ${perfDevice === 'mobile' ? 'text-workspace-accent' : 'text-muted-foreground'}`}
              title="Mobile"
            >
              <Smartphone className="w-4 h-4" />
            </button>
            <button
              onClick={() => onSetPerfDevice('desktop')}
              className={`flex items-center justify-center w-9 h-7 text-[10px] font-bold transition-all relative z-10 ${perfDevice === 'desktop' ? 'text-workspace-accent' : 'text-muted-foreground'}`}
              title="Desktop"
            >
              <Monitor className="w-4 h-4" />
            </button>
            <motion.div
              layoutId="perf-active-header"
              className="absolute inset-y-1 w-[calc(50%-4px)] bg-background border border-border shadow-sm rounded-lg"
              animate={{ x: perfDevice === 'mobile' ? 0 : '100%' }}
              transition={{ type: "spring", bounce: 0.15, duration: 0.5 }}
            />
          </div>

          {perfData[perfDevice]?.fetchedAt && (
            <>
              <div className="h-6 w-px bg-border mx-1" />
              <a
                href={`https://pagespeed.web.dev/report?url=${encodeURIComponent(siteUrl)}&strategy=${perfDevice}`}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 h-9 px-4 rounded-xl bg-foreground hover:bg-foreground/90 text-[10px] font-black uppercase tracking-wider text-background shadow-lg shadow-foreground/5 transition-all group"
              >
                Full Report
                <ArrowUpRight className="w-3.5 h-3.5 text-background/70 group-hover:text-background transition-colors" />
              </a>
            </>
          )}
        </div>
      </div>

      <div className={`grid grid-cols-2 lg:grid-cols-5 gap-x-4 gap-y-8 pb-4 flex-1 items-center transition-opacity ${loadingPerf ? 'opacity-40 blur-[1px]' : 'opacity-100'}`}>
        {[
          { label: 'Performance', val: scores.perf },
          { label: 'Accessibility', val: scores.acc },
          { label: 'Best Practices', val: scores.best },
          { label: 'SEO', val: scores.seo }
        ].map(item => (
          <div key={item.label} className="flex flex-col items-center">
            <div className="h-[60px] flex items-center justify-center">
              <CircularProgress score={item.val} size={60} strokeWidth={5} />
            </div>
            <p className="mt-4 text-[10px] font-black uppercase tracking-wider text-muted-foreground text-center line-clamp-1">{item.label}</p>
          </div>
        ))}
        <div className="flex flex-col items-center">
          <div className="h-[60px] flex items-center justify-center">
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-orange-50 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400 font-bold text-xs border border-orange-200 dark:border-orange-500/20 shadow-sm">
              <div className="w-2 h-2 bg-orange-500 dark:bg-orange-400 rounded-[2px]" />
              Coming Soon
            </div>
          </div>
          <p className="mt-4 text-[10px] font-black uppercase tracking-wider text-muted-foreground text-center line-clamp-1">Agentic Browsing</p>
        </div>
      </div>
    </div>
  );
};
